import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class CompletePurchaseBlock extends HTMLElement {
  connectedCallback() {
    this.products = this.#readProducts();
    this.cards = Array.from(this.querySelectorAll('[data-complete-purchase-card]'));
    this.button = this.querySelector('[data-complete-purchase-button]');
    this.status = this.querySelector('[data-complete-purchase-status]');

    this.addEventListener('change', this.#handleChange);
    this.button?.addEventListener('click', this.#handleAddToCart);

    this.cards.forEach((card) => this.#syncCard(card));
    this.#updateButton();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.button?.removeEventListener('click', this.#handleAddToCart);
  }

  #readProducts() {
    const script = this.querySelector('[data-complete-purchase-products]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Complete purchase products could not be parsed.', error);
      return [];
    }
  }

  #handleChange = (event) => {
    const card = event.target.closest('[data-complete-purchase-card]');
    if (!card) return;

    this.#syncCard(card);
    this.#updateButton();
  };

  #syncCard(card) {
    const product = this.#getProduct(card);
    if (!product) return;

    this.#syncOptions(card, product);
    const variant = this.#getSelectedVariant(card, product) || this.#getFirstPurchasableVariant(product) || product.variants[0];

    if (variant) {
      this.#setCardVariant(card, product, variant);
    }
  }

  #syncOptions(card, product) {
    const selects = Array.from(card.querySelectorAll('[data-complete-purchase-option]'));
    selects.forEach((select) => {
      Array.from(select.options).forEach((option) => {
        const optionIndex = Number(select.dataset.optionIndex);
        const candidateOptions = this.#getSelectedOptions(card);
        candidateOptions[optionIndex] = option.value;

        const matchingVariants = product.variants.filter((variant) =>
          variant.options.every((value, index) => value === candidateOptions[index])
        );
        const hasPurchasableMatch = matchingVariants.some((variant) => this.#isPurchasable(variant));

        option.disabled = !hasPurchasableMatch;
      });

      if (select.selectedOptions[0]?.disabled) {
        const firstEnabled = Array.from(select.options).find((option) => !option.disabled);
        if (firstEnabled) select.value = firstEnabled.value;
      }
    });
  }

  #setCardVariant(card, product, variant) {
    card.dataset.variantId = variant.id;
    card.dataset.variantPrice = variant.price;

    variant.options.forEach((value, index) => {
      const select = card.querySelector(`[data-complete-purchase-option][data-option-index="${index}"]`);
      if (select) select.value = value;
    });

    const checkbox = card.querySelector('[data-complete-purchase-checkbox]');
    const purchasable = this.#isPurchasable(variant);
    if (checkbox) {
      checkbox.disabled = !purchasable;
      if (!purchasable) checkbox.checked = false;
    }

    const price = card.querySelector('[data-complete-purchase-price]');
    if (price) price.textContent = variant.priceFormatted;

    const comparePrice = card.querySelector('[data-complete-purchase-compare-price]');
    if (comparePrice) {
      comparePrice.textContent = variant.compareAtPrice > variant.price ? variant.compareAtPriceFormatted : '';
      comparePrice.hidden = !(variant.compareAtPrice > variant.price);
    }

    const image = card.querySelector('[data-complete-purchase-image]');
    if (image && variant.image) {
      image.src = variant.image;
      image.srcset = '';
      image.alt = variant.imageAlt || product.title;
    }

    card.toggleAttribute('data-unavailable', !purchasable);
  }

  #getSelectedOptions(card) {
    return Array.from(card.querySelectorAll('[data-complete-purchase-option]')).map((select) => select.value);
  }

  #getSelectedVariant(card, product) {
    const selectedOptions = this.#getSelectedOptions(card);
    if (!selectedOptions.length) {
      return product.variants.find((variant) => String(variant.id) === card.dataset.variantId);
    }

    return product.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    );
  }

  #getFirstPurchasableVariant(product) {
    return product.variants.find((variant) => this.#isPurchasable(variant));
  }

  #isPurchasable(variant) {
    if (!this.allowContinueSelling && variant.inventoryManagement && variant.inventoryQuantity <= 0) return false;
    if (variant.available) return true;
    if (!this.allowContinueSelling) return false;

    return variant.inventoryPolicy === 'continue';
  }

  #getProduct(card) {
    return this.products.find((product) => String(product.id) === card.dataset.productId);
  }

  #getSelectedItems() {
    return this.cards
      .filter((card) => card.querySelector('[data-complete-purchase-checkbox]')?.checked)
      .map((card) => {
        const product = this.#getProduct(card);
        const variant = product ? this.#getSelectedVariant(card, product) : null;

        if (!variant || !this.#isPurchasable(variant)) return null;

        return {
          id: Number(variant.id),
          quantity: 1,
          price: Number(variant.price),
          productId: product.id,
        };
      })
      .filter(Boolean);
  }

  #updateButton() {
    const items = this.#getSelectedItems();
    const count = items.length;
    const total = items.reduce((sum, item) => sum + item.price, 0);

    if (!this.button) return;

    this.button.disabled = count === 0;

    if (count === 0) {
      this.button.textContent = this.emptyText;
      return;
    }

    const template = count === 1 ? this.singleText : this.multipleText;
    this.button.textContent = template
      .replace(/\{count\}/g, count.toString())
      .replace(/\{total\}/g, this.#formatMoney(total));
  }

  #handleAddToCart = async () => {
    const items = this.#getSelectedItems();
    if (!items.length || !this.button) return;

    this.#setState('loading');

    try {
      const sections = this.#getCartSectionIds();
      const response = await fetch(this.#cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({ id: item.id, quantity: item.quantity })),
          sections: sections.join(','),
        }),
      });
      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw payload;
      }

      const cart = await this.#fetchCart();
      this.dispatchEvent(
        new CartAddEvent(cart ?? payload, this.id, {
          source: 'complete-purchase-block',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: payload.sections,
        })
      );

      this.#setState('success');
      this.cards.forEach((card) => {
        const checkbox = card.querySelector('[data-complete-purchase-checkbox]');
        if (checkbox) checkbox.checked = false;
      });
      this.#updateButton();
    } catch (error) {
      const message = error?.message || error?.description || this.errorText;
      this.dispatchEvent(new CartErrorEvent(this.id, message, error?.description, error?.errors));
      this.#setState('error', message);
    }
  };

  #setState(state, message = '') {
    if (!this.button) return;

    this.dataset.state = state;
    this.button.disabled = state === 'loading';

    if (state === 'loading') {
      this.button.textContent = this.loadingText;
    } else if (state === 'success') {
      this.button.textContent = this.successText;
    } else if (state === 'error') {
      this.button.textContent = this.errorText;
    }

    if (this.status) {
      this.status.textContent = state === 'error' ? message : state === 'success' ? this.successText : '';
    }

    if (state === 'success' || state === 'error') {
      window.setTimeout(() => {
        this.dataset.state = 'idle';
        if (this.status) this.status.textContent = '';
        this.#updateButton();
      }, 3500);
    }
  }

  #formatMoney(value) {
    return formatMoney(value, this.moneyFormat, this.currency);
  }

  #getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
  }

  async #fetchCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  get allowContinueSelling() {
    return this.dataset.allowContinueSelling === 'true';
  }

  get moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  get currency() {
    return this.dataset.currency || 'USD';
  }

  get emptyText() {
    return this.dataset.emptyText || 'Select products to add';
  }

  get singleText() {
    return this.dataset.singleText || 'Add {count} product — {total}';
  }

  get multipleText() {
    return this.dataset.multipleText || 'Add {count} products — {total}';
  }

  get loadingText() {
    return this.dataset.loadingText || 'Adding...';
  }

  get successText() {
    return this.dataset.successText || 'Products added to cart';
  }

  get errorText() {
    return this.dataset.errorText || 'Could not add products. Please try again.';
  }

  get #cartAddUrl() {
    return window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  }
}

if (!customElements.get('complete-purchase-block')) {
  customElements.define('complete-purchase-block', CompletePurchaseBlock);
}
